import React, { useState } from 'react';
import { BsLink } from 'react-icons/bs';
import { BiPencil, BiPlus } from 'react-icons/bi';
import DeleteProfileDetails from './DeleteProfileDetails';
import useDisclosure from 'src/hooks/utils/useDisclosure';
import { useMutation, useQueryClient } from 'react-query';
import { useToast } from 'src/context/ToastProvider';
import ReadMoreText from '../shared/ReadMoreText';
import EditProfileProject from './EditProfileProject';
import { UIUserProjectDetails, UserProjectDetail } from 'types';
import { deleteUserProjectFn } from 'src/apis/profile';
import { get } from 'lodash';
import ProfileContentHeader from './ProfileContentHeader';
import { validateUrl } from 'shared-utils';

interface Props {
	title: string;
	editing?: boolean;
	userProjects?: any;
	profileId?: string;
	id?: string;
	isSelf?: boolean | null;
	isPublicProfile: boolean;
}

const ProfileProjects = ({ title, isSelf, isPublicProfile, editing = false, userProjects, profileId, id }: Props) => {
	const toast = useToast();
	const queryClient = useQueryClient();
	const [isEditing, setIsEditing] = useState(false);
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [deletingProjectIndex, setDeletingProjectIndex] = useState(-1);
	const [deleteModalStates, setDeleteModalStates] = useState<Array<boolean>>(userProjects.map(() => false));
	const [selectedProjectId, setSelectedProjectId] = useState<string>('');
	const [userProjectDetails, setUserProjectDetails] = useState<UserProjectDetail>({
		description: '',
		endedAt: new Date(),
		link: '',
		startedFrom: new Date(),
		title: '',
	});

	const handleGithubClick = (github_url: string) => {
		window.open(github_url, '_blank');
	};

	const handleEditProjectClick = () => {
		onOpen();
	};

	const deleteProjectDetailFn = useMutation({
		mutationKey: 'deleteProjectDetail',
		mutationFn: deleteUserProjectFn,
		onSuccess() {
			queryClient.refetchQueries(['getUserProfile']);
			toast({
				title: 'Project deleted successfully',
				type: 'success',
			});

			const newStates = [...deleteModalStates];
			newStates[deletingProjectIndex] = false;
			setDeleteModalStates(userProjects.map(() => false));
			setDeletingProjectIndex(-1);
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				type: 'error',
				title: errorMessage,
			});
		},
	});

	const handleProjectDetailsDelete = (projectId: string, index: number) => {
		setDeletingProjectIndex(index);
		deleteProjectDetailFn.mutate(projectId);
	};

	const handleAddProject = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		event.stopPropagation();
		setUserProjectDetails({
			description: '',
			endedAt: new Date(),
			link: '',
			startedFrom: new Date(),
			title: '',
		});
		setIsEditing(false);
		onOpen();
	};

	return (
		<>
			<ProfileContentHeader
				data={{ name: title, isImp: false }}
				extraContent={
					isSelf ? (
						<div className="ml-auto flex grow-0 items-center justify-end bg-transparent pb-1 mr-1 mt-1">
							{/* Add new Project button */}
							<div
								onClick={event => handleAddProject(event)}
								className="flex items-center justify-center gap-[4px] rounded-[4px] border border-[#C7C9D9] px-[4px] py-[2px] text-[11px] text-[#111928] dark:text-[white] hover:cursor-pointer hover:border-[black] bg-white dark:bg-inherit dark:hover:bg-[white] hover:!text-[black]"
							>
								<BiPlus size={15} />
							</div>
						</div>
					) : null
				}
			/>
			<div
				id={id}
				className="flex flex-col gap-2 overflow-hidden bg-transparent bg-white dark:bg-[#1F2A37] rounded-[8px] relative"
			>
				{userProjects && userProjects.length !== 0 ? (
					<div className="flex grow flex-col gap-[14px] px-1">
						{userProjects.map((userProject: UIUserProjectDetails, index: number) => {
							return (
								<div
									key={userProject._id}
									className="bg-white p-3 dark:bg-gray-800 md:group-hover:show-icons-on-hover group flex w-full flex-col gap-[2px] whitespace-normal break-words font-inter "
								>
									<div className="flex w-full justify-between">
										<p className="max-w-[90%] whitespace-normal break-words text-[16px] font-[500] text-[#28293D] dark:text-[#F2F2F5]">
											{userProject.title}
										</p>
										{isSelf ? (
											<div className="edit-delete-icon text-[#28293D] dark:text-[#c2c3db] sm:flex sm:gap-2 md:hidden md:group-hover:flex md:group-hover:gap-3">
												<div>
													<BiPencil
														onClick={e => {
															e.stopPropagation();
															setSelectedProjectId(userProject._id);
															setUserProjectDetails({
																description: userProject.description || '',
																endedAt: userProject.endedAt || new Date(),
																link: userProject.link || '',
																startedFrom: userProject.startedFrom || new Date(),
																title: userProject.title || '',
															});
															setIsEditing(true);
															handleEditProjectClick();
														}}
														className="cursor-pointer"
													/>
												</div>
												<div>
													<DeleteProfileDetails
														onDelete={handleProjectDetailsDelete}
														id={userProject._id}
														index={index}
														isDeleteModalOpen={deleteModalStates[index]}
														setIsDeleteModalOpen={isOpen => {
															const newStates = [...deleteModalStates];
															newStates[index] = isOpen;
															setDeleteModalStates(newStates);
														}}
														isLoading={deleteProjectDetailFn.isLoading}
													/>
												</div>
											</div>
										) : null}
									</div>
									<div
										className={`${
											editing ? 'max-w-[90%]' : 'max-w-[100%]'
										} whitespace-normal break-words text-[14px] font-[400] text-[#555770] dark:text-[#C7C9D9]`}
									>
										{userProject.description && userProject.description.length > 250 ? (
											<ReadMoreText maxChars={220} work_description={userProject.description || ''} />
										) : (
											<p>{userProject.description}</p>
										)}
									</div>
									{userProject.link && validateUrl(userProject.link) && (
										<p className="flex items-center gap-1 text-[#7777E7] dark:text-[#6698FA] ml-auto">
											<BsLink />
											<span onClick={() => handleGithubClick(userProject.link || '')} className="cursor-pointer">
												Project Link
											</span>
										</p>
									)}
								</div>
							);
						})}
					</div>
				) : (
					<div className="mx-auto max-w-[600px] px-4 py-5 text-center font-inter text-[14px] font-[500] text-[#28293D] dark:text-[#C7C9D9]">
						{isSelf && !isPublicProfile
							? 'Got some mind-blowing side projects, epic case studies, or mind-bending product teardowns? We want to see them all!'
							: "There's nothing to show."}
					</div>
				)}
				<EditProfileProject
					selectedProfileId={selectedProjectId}
					isOpen={isOpen}
					onClose={onClose}
					projectDetails={userProjectDetails}
					isEditing={isEditing}
				/>
			</div>
		</>
	);
};

export default ProfileProjects;
